import { flushSync } from 'react-dom';

type ViewTransitionHandle = {
  finished: Promise<void>;
  ready?: Promise<void>;
  updateCallbackDone?: Promise<void>;
};

type ViewTransitionDocument = Document & {
  startViewTransition?: (
    update:
      | (() => void)
      | {
          update: () => void;
          types?: string[];
        },
  ) => ViewTransitionHandle;
};

export type ScreenTransitionDirection = 'forward' | 'backward' | 'replace';

export function runScreenTransition(
  update: () => void,
  direction: ScreenTransitionDirection = 'replace',
): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    update();
    return;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDeterministicSmoke = import.meta.env.VITE_MOCK_DETERMINISTIC === 'true';
  const isTransitionDisabled = import.meta.env.VITE_DISABLE_VIEW_TRANSITIONS === 'true';
  const transitionDocument = document as ViewTransitionDocument;
  const startViewTransition = transitionDocument.startViewTransition?.bind(transitionDocument);

  if (!startViewTransition || prefersReducedMotion || isDeterministicSmoke || isTransitionDisabled) {
    update();
    dispatchScreenTransitionComplete();
    return;
  }

  const updateDom = () => {
    flushSync(update);
  };

  let transition: ViewTransitionHandle | null = null;

  try {
    dispatchScreenTransitionStart();
    transition = startViewTransition({
      update: updateDom,
      types: [direction],
    });
  } catch {
    try {
      dispatchScreenTransitionStart();
      transition = startViewTransition(updateDom);
    } catch {
      update();
      dispatchScreenTransitionComplete();
      return;
    }
  }

  void transition.ready?.catch(() => undefined);
  void transition.updateCallbackDone?.catch(() => undefined);
  void transition.finished.then(dispatchScreenTransitionComplete, dispatchScreenTransitionComplete);
}

function dispatchScreenTransitionStart(): void {
  window.dispatchEvent(new Event('elmental:screen-transition-start'));
}

function dispatchScreenTransitionComplete(): void {
  window.dispatchEvent(new Event('elmental:screen-transition-complete'));
}
